import { getApiKey } from "./storage";
import { md5Base64 } from "./geo";

export type UploadResponse = {
  signed_id: string;
  direct_upload: { url: string; headers: Record<string, string> };
};

export type CreateNoticeInput = {
  registration: string;
  latitude: number;
  longitude: number;
  start_date: string;
  tbnr?: string;
  note?: string;
  photo_signed_id?: string;
};

export type NoticeResponse = {
  token: string;
  registration: string;
  tbnr: string | null;
  status: string;
};

class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly body: string) {
    super(message);
  }
}

function authHeaders(): Record<string, string> {
  const key = getApiKey();
  if (!key) throw new Error("API-Key fehlt — bitte in den Einstellungen setzen.");
  return { "X-API-KEY": key };
}

async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { ...authHeaders(), "Content-Type": "application/json", ...(init.headers || {}) },
  });
  const body = await res.text();
  if (!res.ok) throw new ApiError(`weg.li ${path} failed`, res.status, body);
  return body ? (JSON.parse(body) as T) : ({} as T);
}

export async function uploadPhoto(file: File): Promise<string> {
  const contentType = file.type || "image/jpeg";
  const checksum = await md5Base64(file);
  const upload = await apiJson<UploadResponse>("/uploads", {
    method: "POST",
    body: JSON.stringify({
      upload: {
        filename: file.name || `photo-${Date.now()}.jpg`,
        byte_size: file.size,
        checksum,
        content_type: contentType,
      },
    }),
  });

  // Rails 8.1 ActiveStorage signs the GCS V2 URL with empty Content-Type, so
  // the PUT must not send one. Wrapping File in a typeless Blob prevents
  // fetch() from auto-populating Content-Type from file.type.
  const gcs = await fetch(upload.direct_upload.url, {
    method: "PUT",
    headers: upload.direct_upload.headers,
    body: new Blob([file]),
  });
  if (!gcs.ok) {
    const body = await gcs.text().catch(() => "");
    const signed = Object.keys(upload.direct_upload.headers).join(",");
    throw new Error(
      `GCS upload ${gcs.status}. ct=${contentType} size=${file.size} md5=${checksum} hdrs=[${signed}] body=${body.slice(0, 400)}`,
    );
  }

  return upload.signed_id;
}

export async function createNotice(input: CreateNoticeInput): Promise<NoticeResponse> {
  const notice: Record<string, unknown> = {
    registration: input.registration,
    latitude: input.latitude,
    longitude: input.longitude,
    start_date: input.start_date,
  };
  if (input.photo_signed_id) notice.photos = [input.photo_signed_id];
  if (input.tbnr) notice.tbnr = input.tbnr;
  if (input.note) notice.note = input.note;

  return apiJson<NoticeResponse>("/notices", {
    method: "POST",
    body: JSON.stringify({ notice }),
  });
}

export async function pingAuth(): Promise<boolean> {
  try {
    const res = await fetch("/api/notices?per_page=1", { headers: authHeaders() });
    return res.ok;
  } catch {
    return false;
  }
}
